import {
  IbulkDelete,
  IbulkDeleteSuccessResponse,
  IdataArrayResponse,
  IdeleteOneResponse,
  Ifile,
  IfilterProps, Isuccess, TbackEndGeneratedFields,
  TbackEndGeneratedFieldsExcludingId
} from '@biz-nest/core-client';
import { lastValueFrom } from 'rxjs';
import { IvendorManger } from '../../../shared-types';
import { ManagerClient } from '../../utils/manager-client';
import { ImanagerBaseOmit, ManagerBase } from './delivery-manager.define';

/**
 * Type representing a vendor manager request.
 */
export type TvendorManagerRequest<
  CreateMode extends boolean = false, U = Omit<Partial<IvendorManger>,
  TbackEndGeneratedFieldsExcludingId | 'manager'> & ImanagerBaseOmit<CreateMode>> =
  CreateMode extends true ? Omit<
  IvendorManger, TbackEndGeneratedFields | 'manager'> & ImanagerBaseOmit<CreateMode> : U;

/**
 * Class representing a vendor manager.
 */
export class VendorManager
  extends ManagerBase {
  /**
   * The base URL for the vendor manager.
   */
  static baseUrl = '/vendormanager';

  /**
   * The unique reference ID.
   */
  override baseUrl = '/vendormanager';

  /**
   * The companies assigned to the vendor manager.
   */
  companysAssigned: string[];

  constructor(data: IvendorManger) {
    super(data);
    this.companysAssigned = data.companysAssigned || [];
  }

  /**
   * Retrieves a list of vendor managers.
   *
   * @param {number} [offset=0] - The offset to start retrieving vendor managers from.
   * @param {number} [limit=0] - The limit of vendor managers to retrieve.
   *
   * @returns {Promise<{count: number, dataArr: VendorManager[]}>} - A promise
   *  that resolves to an object containing the total count of vendor managers
   * and an array of vendor manager objects.
   */
  static async getAll(offset = 0, limit = 0) {
    const query = new URLSearchParams({
      offset: offset.toString(),
      limit: limit.toString()
    }).toString();
    const observer$ = ManagerClient.ehttp
      .makeGet<IdataArrayResponse<IvendorManger>>(`${VendorManager.baseUrl}?${query}`);
    const vendormanagers = await lastValueFrom(observer$);

    return {
      count: vendormanagers.count,
      dataArr: vendormanagers.data
        .map(val => new VendorManager(val)) };
  }

  /**
   * Retrieves a list of vendor managers based on a filter.
   *
   * @param {IfilterProps} filter - The filter to apply to the vendor managers.
   *
   * @returns {Promise<{count: number, dataArr: VendorManager[]}>} - A promise
   *  that resolves to an object containing the total count of vendor managers
   * and an array of vendor manager objects.
   */
  static async filterAll(filter: IfilterProps) {
    const observer$ = ManagerClient.ehttp
      .makePost<IdataArrayResponse<IvendorManger>>(`${VendorManager.baseUrl}/filter`, filter);
    const vendormanagers = await lastValueFrom(observer$);

    return {
      count: vendormanagers.count,
      dataArr: vendormanagers.data
        .map(val => new VendorManager(val)) };
  }

  /**
   * Retrieves a vendor manager by ID.
   *
   * @param {string} resourceId - The ID of the vendor manager to retrieve.
   *
   * @returns {Promise<VendorManager>} - A promise that resolves to a vendor manager object.
   */
  static async getOne(resourceId: string) {
    const observer$ = ManagerClient.ehttp
      .makeGet<IvendorManger>(`${VendorManager.baseUrl}/${resourceId}`);
    const vendormanager = await lastValueFrom(observer$);

    return new VendorManager(vendormanager);
  }

  /**
   * Retrieves the current user's profile.
   *
   * @returns {Promise<VendorManager>} - A promise that resolves to a vendor manager object.
   */
  static async myProfile() {
    const observer$ = ManagerClient.ehttp
      .makePost<IvendorManger>(`${VendorManager.baseUrl}/my-profile`, {});
    const vendormanager = await lastValueFrom(observer$);

    return new VendorManager(vendormanager);
  }

  /**
   * Deletes multiple vendor managers based on the provided IDs.
   *
   * @param {IbulkDelete} vals - The IDs of the vendor managers to delete.
   *
   * @returns {Promise<IbulkDeleteSuccessResponse>} - A promise that resolves to a success object.
   */
  static bulkDelete(vals: IbulkDelete) {
    const observer$ = ManagerClient.ehttp
      .makePost<IbulkDeleteSuccessResponse>(`${VendorManager.baseUrl}/bulk-delete`, vals);

    return lastValueFrom(observer$);
  }

  /**
   * Validates a vendor manager invitation token.
   * @param {Object} payload - The payload containing the token to validate.
   * @param {string} payload.token - The invitation token to validate.
   * @returns {Promise<Isuccess>} The result of the validation operation.
   */
  static validateInvitation(payload: { token: string }): Promise<Isuccess> {
    const observer$ = ManagerClient.ehttp
      .makePost<Isuccess>(`${VendorManager.baseUrl}/validate-invitation`, payload);

    return lastValueFrom(observer$);
  }

  /**
   * Adds a new vendor manager.
   *
   * @param {TvendorManagerRequest<true>} payload - The payload containing the vendor manager data.
   *
   * @returns {Promise<VendorManager>} - A promise that resolves to a vendor manager object.
   */
  static async add(payload: TvendorManagerRequest<true>) {
    const observer$ = ManagerClient.ehttp.makePost<IvendorManger>(`${VendorManager.baseUrl}`, payload);

    return new VendorManager(await lastValueFrom(observer$));
  }

  /**
     * Uploads multiple files to the vendormanager member.
     *
     * @param {Ifile[]} files - The files to upload.
     * @returns {Promise<Isuccess>} The result of the file upload operation.
     */
  uploadImages(files: Ifile[]) {
    return ManagerClient.ehttp
      .uploadFiles<IvendorManger>(files, `${VendorManager.baseUrl}/upload-images`, { _id: this._id });
  }

  /**
     * Uploads profilePicture associated with the current VendorManager instance.
     * @param file - The image to upload.
     * @returns An observable that emits a success object.
     */
  uploadProfilePicture(file: Ifile) {
    return ManagerClient.ehttp
      .uploadFiles<IvendorManger>([file], `${VendorManager.baseUrl}/upload-profile-picture`, { _id: this._id });
  }

  /**
     * Uploads cover pic associated with the current VendorManager instance.
     * @param file - The image to upload.
     * @returns An observable that emits a success object.
     */
  uploadProfileCoverPicture(file: Ifile) {
    return ManagerClient.ehttp
      .uploadFiles<IvendorManger>([file], `${VendorManager.baseUrl}/upload-profile-cover-picture`, { _id: this._id });
  }

  /**
   * Sets an image as the profile picture or cover picture.
   *
   * @param {string} fileId - The ID of the file to set as the image.
   * @param {'profilePicture' | 'profileCoverPicture'} field - The field to set the image as.
   *
   * @returns {Promise<VendorManager>} The result of the image setting operation.
   */
  async setImageAs(
    fileId: string,
    field: 'profilePicture' | 'profileCoverPicture'
  ) {
    const observer$ = ManagerClient.ehttp
      .makePost<IvendorManger>(`${VendorManager.baseUrl}/set-image-as`, { fileId, field });

    return new VendorManager(await lastValueFrom(observer$));
  }

  /**
     * Deletes images associated with the current user.
     *
     * @param {string[]} fileIds - The IDs of the files to delete.
     * @returns {Promise<VendorManager>} The result of the deletion operation.
     */
  async deleteImages(fileIds: string[]) {
    const observer$ = ManagerClient.ehttp
      .makePost<IvendorManger>(this.baseUrl + '/delete-images', { fileIds, _id: this._id });

    return new VendorManager(await lastValueFrom(observer$));
  }


  /**
   * Updates the vendor manager.
   *
   * @param {TvendorManagerRequest} payload - The payload containing the vendor manager data.
   *
   * @returns {Promise<VendorManager>} - A promise that resolves to a vendor manager object.
   */
  async update(payload: TvendorManagerRequest) {
    const observer$ = ManagerClient.ehttp.makePut<IvendorManger>(`${VendorManager.baseUrl}`, payload);

    return new VendorManager(await lastValueFrom(observer$));
  }

  /**
   * Removes the vendor manager.
   *
   * @returns {Promise<IdeleteOneResponse | null>} - A promise that resolves to a delete one response object.
   */
  remove() {
    const observer$ = ManagerClient.ehttp
      .makeDelete<IdeleteOneResponse | null>(`${VendorManager.baseUrl}/${this._id}`);

    return lastValueFrom(observer$);
  }
}
